import {
  ICombinationMedia,
  SlideMedia,
  ProductCombinationMedias,
  ProductMedia,
  ProductMedias,
  HeroCarouselMedias,
  IVideo,
} from '../../../../types/interfaces';

import {
  CommonCloudinaryTransformer,
  PosterImageCloudinaryTransformer,
  ReferentialCloudinaryTransformer,
} from './CloudinaryTransformer';
import { ThumbnailWidth, GalleryWidth, ZoomWidth } from './productPageDimensions';
import { FetchAutoTransformation } from './SpecificTransformations';

export class UrlFetchCloudinaryTransformer implements BaseMediaTransformer {
  private static readonly CLOUDINARY_FETCH_BASE_URL =
    'https://cms.cloudinary.vpsvc.com/image/fetch';

  constructor() {}

  private transformUrl(url: string, width: number): string {
    if (url.includes('cloudinary')) {
      return url;
    }

    const transformation = new FetchAutoTransformation(width);
    const encodedUrl = encodeURIComponent(url);

    return `${UrlFetchCloudinaryTransformer.CLOUDINARY_FETCH_BASE_URL}/${transformation.toString()}/${encodedUrl}`;
  }

  private transformImage(image: string): {
    thumbnail: string;
    gallery: string;
    zoom: string;
    altText?: string;
  } {
    return {
      thumbnail: this.transformUrl(image, ThumbnailWidth),
      gallery: this.transformUrl(image, GalleryWidth),
      zoom: this.transformUrl(image, ZoomWidth),
    };
  }

  public transformProductPageMedias(productPageMedias: ProductMedias): HeroCarouselMedias {
    return {
      common: productPageMedias.common.map((media) => this.transformMedia(media)),
      combinationMedias: productPageMedias.combinationMedias.map((combinationMedia) =>
        this.transformCombinationMedia(combinationMedia)
      ),
    };
  }

  private transformCombinationMedia(combinationMedia: ProductCombinationMedias): ICombinationMedia {
    return {
      combination: combinationMedia.combination,
      medias: combinationMedia.medias.map((media) => this.transformMedia(media)),
    };
  }

  private transformMedia(media: ProductMedia): SlideMedia {
    if (media.video) {
      return {
        video: {
          id: media.video.publicId,
          deliveryUrl: media.video.deliveryUrl,
          altText: media.video.altText,
          autoplay: media.video.autoplay,
          loop: media.video.loop,
          name: media.video.videoName,
          description: media.video.videoDescription,
          posterImage: media.video.posterImage,
          smallPosterImage: media.video.smallPosterImageFrame,
        },
      };
    }

    return {
      image: { ...this.transformImage(media.image!.url), altText: media.image.altText },
    };
  }
}

export class AlreadyTransformedMediasTransformer implements BaseMediaTransformer {
  constructor() {}

  public transformProductPageMedias(productPageMedias: ProductMedias): HeroCarouselMedias {
    return {
      common: productPageMedias.common.map((media) => this.transformMedia(media)),
      combinationMedias: productPageMedias.combinationMedias.map((combinationMedia) =>
        this.transformCombinationMedia(combinationMedia)
      ),
    };
  }

  private transformCombinationMedia(combinationMedia: ProductCombinationMedias): ICombinationMedia {
    return {
      combination: combinationMedia.combination,
      medias: combinationMedia.medias.map((media) => this.transformMedia(media)),
    };
  }

  private transformMedia(media: ProductMedia): SlideMedia {
    const oldFormat = media as unknown as SlideMedia;
    return { ...oldFormat };
  }
}

export class CloudinaryMediaTransformer implements BaseMediaTransformer {
  public transformProductPageMedias(productPageMedias: ProductMedias): HeroCarouselMedias {
    return {
      common: this.buildCommonMedias(productPageMedias.common),
      combinationMedias: this.buildReferentialMedias(productPageMedias.combinationMedias),
    };
  }

  private transformVideo(media: { video: IVideo; image?: never }): SlideMedia {
    const cloudinaryTransformer = new PosterImageCloudinaryTransformer(media.video.posterImage);
    return {
      video: {
        id: media.video.publicId,
        deliveryUrl: media.video.deliveryUrl,
        altText: media.video.altText,
        autoplay: media.video.autoplay,
        loop: media.video.loop,
        name: media.video.videoName,
        description: media.video.videoDescription,
        posterImage: cloudinaryTransformer.isValid
          ? cloudinaryTransformer.gallery
          : media.video.defaultPosterImage,
        smallPosterImage: cloudinaryTransformer.isValid
          ? cloudinaryTransformer.thumbnail
          : media.video.smallPosterImageFrame,
      },
    };
  }
  private buildCommonMedias(heroGalleryImages: ProductMedia[]): SlideMedia[] {
    const resultingImages: SlideMedia[] = heroGalleryImages.reduce(
      (heroGalleryMedias: SlideMedia[], media: ProductMedia) => {
        if (media.image) {
          const cloudinaryTransformer = new CommonCloudinaryTransformer(media.image);
          if (cloudinaryTransformer.isValid) {
            heroGalleryMedias.push({
              image: {
                thumbnail: cloudinaryTransformer.thumbnail,
                gallery: cloudinaryTransformer.gallery,
                zoom: cloudinaryTransformer.zoom,
                altText: media.image.altText,
              },
            });
          }
        } else if (media.video) {
          heroGalleryMedias.push(this.transformVideo(media));
        }

        return heroGalleryMedias;
      },
      []
    );
    return resultingImages;
  }

  private buildReferentialMedias(
    combinationMedias: ProductCombinationMedias[]
  ): ICombinationMedia[] {
    const resultingRefImages = [];
    for (const combinationMedia of combinationMedias) {
      const tranformedMedias = combinationMedia.medias.map((media) => {
        if (media.video) {
          return this.transformVideo(media);
        }
        const cloudinaryTransformer = new ReferentialCloudinaryTransformer(media.image!);
        return {
          image: {
            thumbnail: cloudinaryTransformer.thumbnail,
            gallery: cloudinaryTransformer.gallery,
            zoom: cloudinaryTransformer.zoom,
            altText: media.image?.altText,
          },
        };
      });
      resultingRefImages.push({
        combination: combinationMedia.combination,
        medias: tranformedMedias,
      });
    }

    return resultingRefImages;
  }
}

export abstract class BaseMediaTransformer {
  abstract transformProductPageMedias(productPageMedias: ProductMedias): HeroCarouselMedias;
}
